import React, { useState, useEffect, useCallback } from "react";
import { useLanguage, API } from "../App";
import AdminLayout from "../components/AdminLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  ChevronLeft,
  ChevronRight,
  Clock,
  User,
  Plus,
  Lock,
  X,
  Phone,
  Mail,
  Briefcase,
  Ban,
  Calendar as CalendarIcon,
  Users,
  LayoutGrid,
  Sparkles,
  AlertCircle,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { format, startOfWeek, addDays, addWeeks, subWeeks } from "date-fns";
import TeamColumnsView from "../components/calendar/TeamColumnsView";
import WeekView from "../components/calendar/WeekView";
import AgendaView from "../components/calendar/AgendaView";
import CompactView from "../components/calendar/CompactView";
import { useHasFeature } from "../hooks/useFeatureAccess";
import { it, enUS } from "date-fns/locale";

const CalendarView = () => {
  const { t, language } = useLanguage();
  const [appointments, setAppointments] = useState([]);
  const [staff, setStaff] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentWeek, setCurrentWeek] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [selectedStaff, setSelectedStaff] = useState("all");
  const [maxStations, setMaxStations] = useState(1);
  // NEW: view mode (persisted per-user)
  const [viewMode, setViewMode] = useState("team");    // team | week | agenda | compact
  const [viewDate, setViewDate] = useState(new Date()); // current "day" for team/compact
  const [teamFilterServiceId, setTeamFilterServiceId] = useState(null); // smart service filter
  const [agendaQuickRange, setAgendaQuickRange] = useState("week"); // today|tomorrow|week|month
  const [coverageHealth, setCoverageHealth] = useState(null);
  const hasAdvancedCalendar = useHasFeature("advanced_calendar");

  // Dialog states
  const [blockDialogOpen, setBlockDialogOpen] = useState(false);
  const [appointmentDialogOpen, setAppointmentDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  // Inline edit state for the detail dialog
  const [detailEditMode, setDetailEditMode] = useState(false);
  const [detailEditForm, setDetailEditForm] = useState({
    date: "", time: "", staff_id: "", status: "", notes: "",
  });
  const [detailEditSaving, setDetailEditSaving] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);

  // Drag & drop states
  const [draggingAppt, setDraggingAppt] = useState(null); // appointment being dragged
  const [dropTarget,   setDropTarget]   = useState(null); // { date: "yyyy-MM-dd", hour: "HH:00" }

  // Form states
  const [blockForm, setBlockForm] = useState({
    staff_id: "",
    date: "",
    start_time: "",
    end_time: "",
    reason: "",
    stations_used: 1
  });

  const [appointmentForm, setAppointmentForm] = useState({
    service_ids: [], // Support multiple services
    service_id: "", // Legacy single service
    staff_id: "",
    client_first_name: "",
    client_last_name: "",
    client_email: "",
    client_phone: "",
    date: "",
    time: "",
    notes: "",
    stations_used: 1
  });

  const weekDays = [...Array(7)].map((_, i) => addDays(currentWeek, i));
  const hours = [...Array(12)].map((_, i) => `${(i + 8).toString().padStart(2, '0')}:00`);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [staffRes, servicesRes, tenantRes] = await Promise.all([
        axios.get(`${API}/staff`, { withCredentials: true }),
        axios.get(`${API}/services`, { withCredentials: true }),
        axios.get(`${API}/tenants/me`, { withCredentials: true })
      ]);
      setStaff(staffRes.data);
      // back-compat: treat undefined is_active as active (matches backend filter)
      setServices((servicesRes.data || []).filter(s => s.is_active !== false));
      if (tenantRes.data) {
        setMaxStations(tenantRes.data.max_concurrent_bookings || 1);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  }, []);

  const fetchWeekAppointments = useCallback(async () => {
    try {
      const allAppointments = [];
      for (const day of weekDays) {
        const params = { date: format(day, "yyyy-MM-dd") };
        if (selectedStaff && selectedStaff !== "all") params.staff_id = selectedStaff;
        
        const response = await axios.get(`${API}/appointments`, { 
          params,
          withCredentials: true 
        });
        allAppointments.push(...response.data);
      }
      setAppointments(allAppointments);
    } catch (error) {
      console.error("Error fetching appointments:", error);
    } finally {
      setLoading(false);
    }
  }, [weekDays, selectedStaff]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    fetchWeekAppointments();
  }, [currentWeek, selectedStaff, fetchWeekAppointments]);

  // Load view-mode preference from server (per-user)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/calendar-prefs`, { withCredentials: true });
        if (!cancelled) {
          const vm = r.data?.calendar_prefs?.view_mode;
          if (vm && ["team", "week", "agenda", "compact"].includes(vm)) setViewMode(vm);
        }
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // Load coverage health (services without staff) — admin nudge
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/services/coverage/health`, { withCredentials: true });
        if (!cancelled) setCoverageHealth(r.data);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const changeView = async (nextMode) => {
    // Premium gate
    if (!hasAdvancedCalendar && (nextMode === "team" || nextMode === "compact")) {
      toast.info(language === "it"
        ? "Vista premium · inclusa nei piani Pro · upgrade per attivarla"
        : "Premium view · included in Pro plans · upgrade to unlock");
    }
    setViewMode(nextMode);
    try {
      await axios.patch(`${API}/calendar-prefs`,
        { view_mode: nextMode }, { withCredentials: true });
    } catch { /* silent */ }
  };

  // Open the appointment detail dialog and prepare the edit form.
  // Called both from card clicks and (still) the legacy detail entry points.
  const openAppointmentDetail = (appt) => {
    if (!appt) return;
    setSelectedAppointment(appt);
    setDetailEditForm({
      date:     appt.date     || "",
      time:     appt.time     || appt.start_time || "",
      staff_id: appt.staff_id || "",
      status:   appt.status   || "pending",
      notes:    appt.notes    || "",
    });
    setDetailEditMode(false);
    setDetailDialogOpen(true);
  };

  const saveDetailEdit = async () => {
    if (!selectedAppointment) return;
    setDetailEditSaving(true);
    const prev = selectedAppointment;
    // OPTIMISTIC update of the calendar grids
    setAppointments(curr => curr.map(a =>
      a.appointment_id === prev.appointment_id ? { ...a, ...detailEditForm } : a
    ));
    try {
      const r = await axios.patch(
        `${API}/appointments/${prev.appointment_id}`,
        detailEditForm,
        { withCredentials: true }
      );
      // Sync local state with the backend echo (in case staff_name is recomputed)
      const updated = { ...prev, ...detailEditForm, ...(r.data || {}) };
      setSelectedAppointment(updated);
      setDetailEditMode(false);
      toast.success(language === "it" ? "Appuntamento aggiornato" : "Appointment updated");
      fetchWeekAppointments();
    } catch (err) {
      // Rollback optimistic
      setAppointments(curr => curr.map(a =>
        a.appointment_id === prev.appointment_id ? prev : a
      ));
      toast.error(err.response?.data?.detail || (language === "it" ? "Errore aggiornamento" : "Update failed"));
    } finally {
      setDetailEditSaving(false);
    }
  };

  // Move appointment via drag&drop in Week/Compact views (precise snap-15min,
  // no minute preservation — yToTime already returns the exact target time).
  const handleWeekMove = async (appt, newDate, newTime) => {
    if (appt.is_blocked) {
      toast.error(language === "it" ? "Gli slot bloccati non sono spostabili" : "Blocked slots cannot be moved");
      return;
    }
    if (appt.date === newDate && appt.time === newTime) return; // no-op

    const prevDate = appt.date;
    const prevTime = appt.time;
    // OPTIMISTIC
    setAppointments(curr => curr.map(a =>
      a.appointment_id === appt.appointment_id ? { ...a, date: newDate, time: newTime } : a
    ));
    try {
      await axios.patch(`${API}/appointments/${appt.appointment_id}`,
        { date: newDate, time: newTime },
        { withCredentials: true }
      );
      toast.success(language === "it"
        ? `Spostato a ${format(new Date(newDate + "T00:00:00"), "EEE d MMM", { locale: it })} · ${newTime}`
        : `Moved to ${format(new Date(newDate + "T00:00:00"), "EEE d MMM", { locale: enUS })} · ${newTime}`);
      fetchWeekAppointments();
    } catch (err) {
      setAppointments(curr => curr.map(a =>
        a.appointment_id === appt.appointment_id ? { ...a, date: prevDate, time: prevTime } : a
      ));
      toast.error(err.response?.data?.detail || (language === "it" ? "Errore spostamento" : "Move failed"));
    }
  };

  // Move appointment via drag&drop in TeamColumnsView (staff + time change in one gesture)
  const handleTeamMove = async (appt, newStaffId, newTime) => {
    if (appt.is_blocked) {
      toast.error(language === "it" ? "Gli slot bloccati non sono spostabili" : "Blocked slots cannot be moved");
      return;
    }
    const prevStaffId = appt.staff_id;
    const prevTime    = appt.time;
    if (prevStaffId === newStaffId && prevTime === newTime) return; // no-op

    // OPTIMISTIC update
    setAppointments(curr => curr.map(a =>
      a.appointment_id === appt.appointment_id
        ? { ...a, staff_id: newStaffId, time: newTime }
        : a
    ));

    try {
      await axios.patch(`${API}/appointments/${appt.appointment_id}`, {
        staff_id: newStaffId,
        time:     newTime,
      }, { withCredentials: true });
      const movedStaff = staff.find(s => s.staff_id === newStaffId);
      toast.success(language === "it"
        ? `Spostato${movedStaff ? ` su ${movedStaff.name}` : ""} · ${newTime}`
        : `Moved${movedStaff ? ` to ${movedStaff.name}` : ""} · ${newTime}`);
      fetchWeekAppointments();
    } catch (err) {
      // Rollback
      setAppointments(curr => curr.map(a =>
        a.appointment_id === appt.appointment_id
          ? { ...a, staff_id: prevStaffId, time: prevTime }
          : a
      ));
      toast.error(err.response?.data?.detail || (language === "it" ? "Errore spostamento" : "Move failed"));
    }
  };

  const handleBlockSlot = async () => {
    if (!blockForm.staff_id || !blockForm.date || !blockForm.start_time || !blockForm.end_time) {
      toast.error(language === "it" ? "Compila tutti i campi obbligatori" : "Fill all required fields");
      return;
    }

    try {
      await axios.post(`${API}/appointments/block`, blockForm, { withCredentials: true });
      toast.success(language === "it" ? "Slot bloccato!" : "Slot blocked!");
      setBlockDialogOpen(false);
      setBlockForm({ staff_id: "", date: "", start_time: "", end_time: "", reason: "", stations_used: 1 });
      fetchWeekAppointments();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error");
    }
  };

  const handleCreateAppointment = async () => {
    // Validate: need at least one service, staff, client name, date, time
    const serviceIds = appointmentForm.service_ids || [];
    const hasServices = serviceIds.length > 0 || appointmentForm.service_id;
    if (!hasServices || !appointmentForm.staff_id || !appointmentForm.client_first_name || 
        !appointmentForm.client_last_name || !appointmentForm.date || !appointmentForm.time) {
      toast.error(language === "it" ? "Compila tutti i campi obbligatori" : "Fill all required fields");
      return;
    }

    try {
      // Prepare data - use service_ids if available, else fallback to service_id
      const finalServiceIds = serviceIds.length > 0 
        ? serviceIds 
        : [appointmentForm.service_id];
      
      const payload = {
        service_ids: finalServiceIds,
        staff_id: appointmentForm.staff_id,
        client_first_name: appointmentForm.client_first_name,
        client_last_name: appointmentForm.client_last_name,
        client_email: appointmentForm.client_email || null,
        client_phone: appointmentForm.client_phone || null,
        date: appointmentForm.date,
        time: appointmentForm.time,
        notes: appointmentForm.notes || null,
        status: "confirmed"
      };

      await axios.post(`${API}/appointments/manual`, payload, { withCredentials: true });
      toast.success(language === "it" ? "Appuntamento creato!" : "Appointment created!");
      setAppointmentDialogOpen(false);
      setAppointmentForm({
        service_ids: [], service_id: "", staff_id: "", client_first_name: "", client_last_name: "",
        client_email: "", client_phone: "", date: "", time: "", notes: "", stations_used: 1
      });
      fetchWeekAppointments();
    } catch (error) {
      console.error("Create appointment error:", error.response?.data);
      toast.error(error.response?.data?.detail || (language === "it" ? "Errore nella creazione" : "Creation error"));
    }
  };

  const handleCancelAppointment = async (appointmentId) => {
    if (!window.confirm(language === "it" ? "Sei sicuro di voler cancellare questo appuntamento?" : "Are you sure you want to cancel this appointment?")) {
      return;
    }

    try {
      await axios.delete(`${API}/appointments/${appointmentId}`, { withCredentials: true });
      toast.success(language === "it" ? "Appuntamento cancellato!" : "Appointment cancelled!");
      setDetailDialogOpen(false);
      fetchWeekAppointments();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error");
    }
  };

  const handleUnblockSlot = async (blockId) => {
    try {
      await axios.delete(`${API}/appointments/block/${blockId}`, { withCredentials: true });
      toast.success(language === "it" ? "Blocco rimosso!" : "Block removed!");
      setDetailDialogOpen(false);
      fetchWeekAppointments();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error");
    }
  };

  const openBlockDialog = (day, hour) => {
    setBlockForm({
      staff_id: selectedStaff !== "all" ? selectedStaff : (staff[0]?.staff_id || ""),
      date: format(day, "yyyy-MM-dd"),
      start_time: hour,
      end_time: `${(parseInt(hour.split(":")[0]) + 1).toString().padStart(2, '0')}:00`,
      reason: "",
      stations_used: 1
    });
    setBlockDialogOpen(true);
  };

  const openAppointmentDialog = (day, hour) => {
    setAppointmentForm({
      service_ids: [],
      service_id: services[0]?.service_id || "",
      staff_id: selectedStaff !== "all" ? selectedStaff : (staff[0]?.staff_id || ""),
      client_first_name: "",
      client_last_name: "",
      client_email: "",
      client_phone: "",
      date: format(day, "yyyy-MM-dd"),
      time: hour,
      notes: "",
      stations_used: 1
    });
    setAppointmentDialogOpen(true);
  };

  const openDetailDialog = (appt) => {
    openAppointmentDetail(appt);
  };

  const getAppointmentStyle = (appt) => {
    if (appt.is_blocked || appt.status === "blocked") {
      return "bg-gray-500 text-white";
    }
    switch (appt.status) {
      case "confirmed": return "bg-emerald-500 text-white";
      case "pending": return "bg-amber-500 text-white";
      case "awaiting_confirmation": return "bg-orange-500 text-white";
      case "completed": return "bg-blue-500 text-white";
      case "cancelled": return "bg-red-500/50 text-white line-through";
      default: return "bg-accent text-white";
    }
  };

  const getAppointmentsForSlot = (day, hour) => {
    const dateStr = format(day, "yyyy-MM-dd");
    const hourNum = parseInt(hour.split(":")[0]);
    
    return appointments.filter(appt => {
      if (appt.date !== dateStr) return false;
      if (selectedStaff && selectedStaff !== "all" && appt.staff_id !== selectedStaff) return false;
      
      const apptHour = parseInt(appt.time.split(":")[0]);
      return apptHour === hourNum;
    });
  };

  /* ============================================================ */
  /* DRAG & DROP — move appointment to another slot                */
  /* ============================================================ */
  const moveAppointment = async (appt, newDate, newHour) => {
    // Preserve original minutes (so 10:30 dropped on 14:00 row becomes 14:30)
    const origMin = (appt.time || "00:00").split(":")[1] || "00";
    const newTime = `${newHour.split(":")[0]}:${origMin}`;
    if (appt.date === newDate && appt.time === newTime) return; // no-op
    if (appt.is_blocked) {
      toast.error(language === "it" ? "Gli slot bloccati non sono spostabili" : "Blocked slots cannot be moved");
      return;
    }

    const prevDate = appt.date;
    const prevTime = appt.time;

    // OPTIMISTIC update for snappy UX
    setAppointments(curr => curr.map(a =>
      a.appointment_id === appt.appointment_id ? { ...a, date: newDate, time: newTime } : a
    ));

    try {
      await axios.patch(`${API}/appointments/${appt.appointment_id}`,
        { date: newDate, time: newTime },
        { withCredentials: true }
      );
      toast.success(
        language === "it"
          ? `Spostato a ${format(new Date(newDate + "T00:00:00"), "EEE d MMM", { locale: it })} · ${newTime}`
          : `Moved to ${format(new Date(newDate + "T00:00:00"), "EEE d MMM", { locale: enUS })} · ${newTime}`
      );
      // refresh in background to pick up any backend-side recomputation
      fetchWeekAppointments();
    } catch (err) {
      // Rollback
      setAppointments(curr => curr.map(a =>
        a.appointment_id === appt.appointment_id ? { ...a, date: prevDate, time: prevTime } : a
      ));
      toast.error(err.response?.data?.detail || (language === "it" ? "Errore spostamento" : "Move error"));
    }
  };

  const handleDragStart = (e, appt) => {
    if (appt.is_blocked) { e.preventDefault(); return; }
    setDraggingAppt(appt);
    e.dataTransfer.effectAllowed = "move";
    try { e.dataTransfer.setData("text/plain", appt.appointment_id); } catch (_) { /* Safari quirk */ }
    // Use a transparent drag image — we rely on our own visual feedback
    try {
      const img = document.createElement("div");
      img.style.cssText = "position:absolute;top:-9999px;width:1px;height:1px;";
      document.body.appendChild(img);
      e.dataTransfer.setDragImage(img, 0, 0);
      setTimeout(() => document.body.removeChild(img), 0);
    } catch (_) { /* not supported */ }
  };

  const handleDragEnd = () => {
    setDraggingAppt(null);
    setDropTarget(null);
  };

  const handleDragOver = (e, day, hour) => {
    if (!draggingAppt) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    const dateStr = format(day, "yyyy-MM-dd");
    if (!dropTarget || dropTarget.date !== dateStr || dropTarget.hour !== hour) {
      setDropTarget({ date: dateStr, hour });
    }
  };

  const handleDragLeave = (day, hour) => {
    const dateStr = format(day, "yyyy-MM-dd");
    if (dropTarget && dropTarget.date === dateStr && dropTarget.hour === hour) {
      setDropTarget(null);
    }
  };

  const handleDrop = (e, day, hour) => {
    e.preventDefault();
    if (!draggingAppt) return;
    const appt = draggingAppt;
    setDraggingAppt(null);
    setDropTarget(null);
    moveAppointment(appt, format(day, "yyyy-MM-dd"), hour);
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-serif font-medium flex items-center gap-2">
              {t("calendar")}
              {viewMode === "team" && hasAdvancedCalendar && (
                <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-gradient-to-r from-blue-500 to-violet-500 text-white">
                  <Sparkles className="w-2.5 h-2.5" /> Pro
                </span>
              )}
            </h1>
            <p className="text-muted-foreground text-sm">
              {language === "it" ? "Gestisci appuntamenti e blocchi" : "Manage appointments and blocks"}
            </p>
          </div>

          <div className="flex flex-wrap gap-2 items-center">
            {/* VIEW SWITCHER */}
            <div className="flex p-1 rounded-full bg-slate-100 dark:bg-white/5 border border-slate-200 dark:border-white/10" data-testid="calendar-view-switcher">
              {[
                { v: "team",    label: language === "it" ? "Team"      : "Team",    icon: Users,        premium: true  },
                { v: "week",    label: language === "it" ? "Settimana" : "Week",    icon: CalendarIcon, premium: false },
                { v: "agenda",  label: language === "it" ? "Agenda"    : "Agenda",  icon: LayoutGrid,   premium: false },
                { v: "compact", label: language === "it" ? "Compatta"  : "Compact", icon: LayoutGrid,   premium: true  },
              ].map(m => (
                <button key={m.v}
                  onClick={() => changeView(m.v)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-all
                    ${viewMode === m.v
                      ? "bg-white dark:bg-slate-900 shadow text-blue-600"
                      : "text-slate-500 hover:bg-white/60"}`}
                  data-testid={`view-${m.v}`}
                >
                  <m.icon className="w-3.5 h-3.5" /> {m.label}
                  {m.premium && !hasAdvancedCalendar && <span className="text-[8px] font-black text-amber-500">PRO</span>}
                </button>
              ))}
            </div>
            <Button
              variant="outline"
              onClick={() => {
                setBlockForm({ staff_id: staff[0]?.staff_id || "", date: format(new Date(), "yyyy-MM-dd"), start_time: "09:00", end_time: "10:00", reason: "", stations_used: 1 });
                setBlockDialogOpen(true);
              }}
              data-testid="block-slot-btn"
            >
              <Lock className="w-4 h-4 mr-2" />
              {language === "it" ? "Blocca Slot" : "Block Slot"}
            </Button>
            <Button
              onClick={() => {
                setAppointmentForm({
                  service_id: services[0]?.service_id || "",
                  staff_id: staff[0]?.staff_id || "",
                  client_name: "", client_email: "", client_phone: "",
                  date: format(new Date(), "yyyy-MM-dd"),
                  time: "09:00", notes: "", stations_used: 1
                });
                setAppointmentDialogOpen(true);
              }}
              data-testid="add-appointment-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              {language === "it" ? "Nuovo Appuntamento" : "New Appointment"}
            </Button>
          </div>
        </div>

        {/* Setup nudge: services without staff */}
        {hasAdvancedCalendar && coverageHealth?.uncovered_count > 0 && (
          <div className="rounded-2xl border border-amber-200 dark:border-amber-900/40 bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/15 dark:to-orange-900/15 p-3 flex items-start gap-3" data-testid="coverage-warning">
            <div className="w-7 h-7 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shrink-0">
              <AlertCircle className="w-3.5 h-3.5 text-white" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-bold text-amber-800 dark:text-amber-200">
                {language === "it"
                  ? `${coverageHealth.uncovered_count} servizi senza staff assegnato`
                  : `${coverageHealth.uncovered_count} services without assigned staff`}
              </p>
              <p className="text-[11px] text-amber-700 dark:text-amber-300 mt-0.5">
                {language === "it"
                  ? "Configura il team che esegue ogni servizio dal pannello Staff per attivare il filtro intelligente nel booking pubblico."
                  : "Assign the team for each service in the Staff panel to activate smart booking filtering."}
              </p>
            </div>
            <Button size="sm" variant="outline" onClick={() => window.location.assign("/staff")} className="rounded-full whitespace-nowrap shrink-0" data-testid="coverage-fix-btn">
              {language === "it" ? "Configura" : "Configure"}
            </Button>
          </div>
        )}

        {/* TEAM COLUMNS VIEW (Premium) */}
        {viewMode === "team" && (
          <div className="space-y-3">
            <div className="flex items-center justify-between bg-card rounded-xl border p-3">
              <Button variant="ghost" size="icon" onClick={() => setViewDate(addDays(viewDate, -1))}>
                <ChevronLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-3">
                <h2 className="font-medium text-sm">
                  {format(viewDate, "EEEE d MMMM yyyy", { locale: language === "it" ? it : enUS })}
                </h2>
                <Button variant="outline" size="sm" onClick={() => setViewDate(new Date())}>
                  {language === "it" ? "Oggi" : "Today"}
                </Button>
              </div>
              <Button variant="ghost" size="icon" onClick={() => setViewDate(addDays(viewDate, 1))}>
                <ChevronRight className="w-5 h-5" />
              </Button>
            </div>
            <TeamColumnsView
              appointments={appointments}
              staff={staff}
              services={services}
              currentDate={viewDate}
              filterServiceId={teamFilterServiceId}
              onFilterServiceChange={setTeamFilterServiceId}
              onMoveAppointment={handleTeamMove}
              onClickAppointment={(appt) => openAppointmentDetail(appt)}
              onClickSlot={(member, time) => {
                setAppointmentForm({
                  service_id: services[0]?.service_id || "",
                  staff_id: member.staff_id,
                  client_name: "", client_email: "", client_phone: "",
                  date: format(viewDate, "yyyy-MM-dd"),
                  time, notes: "", stations_used: 1
                });
                setAppointmentDialogOpen(true);
              }}
              workingHours={{ start: "08:00", end: "20:00", slotMinutes: 30 }}
              language={language}
            />
          </div>
        )}

        {/* WEEK VIEW (premium swim-lanes) */}
        {viewMode === "week" && (
          <div className="space-y-3">
            <div className="flex items-center justify-between bg-card rounded-xl border p-3">
              <Button variant="ghost" size="icon" onClick={() => setCurrentWeek(subWeeks(currentWeek, 1))} data-testid="week-prev">
                <ChevronLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-3">
                <h2 className="font-medium text-sm">
                  {format(weekDays[0], "d MMM", { locale: language === "it" ? it : enUS })} – {format(weekDays[6], "d MMM yyyy", { locale: language === "it" ? it : enUS })}
                </h2>
                <Button variant="outline" size="sm"
                        onClick={() => setCurrentWeek(startOfWeek(new Date(), { weekStartsOn: 1 }))}
                        data-testid="week-today">
                  {language === "it" ? "Oggi" : "Today"}
                </Button>
              </div>
              <Button variant="ghost" size="icon" onClick={() => setCurrentWeek(addWeeks(currentWeek, 1))} data-testid="week-next">
                <ChevronRight className="w-5 h-5" />
              </Button>
            </div>
            <WeekView
              appointments={appointments}
              staff={staff}
              services={services}
              weekStart={currentWeek}
              filterServiceId={teamFilterServiceId}
              onFilterServiceChange={setTeamFilterServiceId}
              onMoveAppointment={(appt, newDate, newTime) => handleWeekMove(appt, newDate, newTime)}
              onClickAppointment={(appt) => openAppointmentDetail(appt)}
              onClickSlot={(day, time) => {
                setAppointmentForm({
                  service_id: services[0]?.service_id || "",
                  staff_id: staff[0]?.staff_id || "",
                  client_first_name: "", client_last_name: "", client_email: "", client_phone: "",
                  date: format(day, "yyyy-MM-dd"),
                  time, notes: "", stations_used: 1
                });
                setAppointmentDialogOpen(true);
              }}
              workingHours={{ start: "08:00", end: "20:00", slotMinutes: 30 }}
              language={language}
            />
          </div>
        )}

        {/* AGENDA VIEW (chronological list, mobile-first) */}
        {viewMode === "agenda" && (
          <AgendaView
            appointments={appointments}
            staff={staff}
            services={services}
            rangeStart={
              agendaQuickRange === "tomorrow"
                ? addDays(new Date(), 1)
                : agendaQuickRange === "today"
                  ? new Date()
                  : currentWeek
            }
            rangeDays={
              agendaQuickRange === "today" || agendaQuickRange === "tomorrow"
                ? 1
                : agendaQuickRange === "month"
                  ? 30
                  : 7
            }
            quickRange={agendaQuickRange}
            onQuickRangeChange={(r) => {
              setAgendaQuickRange(r);
              if (r === "week" || r === "month") {
                setCurrentWeek(startOfWeek(new Date(), { weekStartsOn: 1 }));
              }
            }}
            filterServiceId={teamFilterServiceId}
            onFilterServiceChange={setTeamFilterServiceId}
            onClickAppointment={(appt) => openAppointmentDetail(appt)}
            onClickSlot={(day) => {
              setAppointmentForm({
                service_id: services[0]?.service_id || "",
                staff_id: staff[0]?.staff_id || "",
                client_first_name: "", client_last_name: "", client_email: "", client_phone: "",
                date: format(day, "yyyy-MM-dd"),
                time: "09:00", notes: "", stations_used: 1
              });
              setAppointmentDialogOpen(true);
            }}
            language={language}
          />
        )}

        {/* COMPACT VIEW (high-density admin) */}
        {viewMode === "compact" && (
          <div className="space-y-3">
            <div className="flex items-center justify-between bg-card rounded-xl border p-3">
              <Button variant="ghost" size="icon" onClick={() => setViewDate(addDays(viewDate, -1))} data-testid="compact-prev">
                <ChevronLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-3">
                <h2 className="font-medium text-sm">
                  {format(viewDate, "EEEE d MMMM yyyy", { locale: language === "it" ? it : enUS })}
                </h2>
                <Button variant="outline" size="sm" onClick={() => setViewDate(new Date())} data-testid="compact-today">
                  {language === "it" ? "Oggi" : "Today"}
                </Button>
              </div>
              <Button variant="ghost" size="icon" onClick={() => setViewDate(addDays(viewDate, 1))} data-testid="compact-next">
                <ChevronRight className="w-5 h-5" />
              </Button>
            </div>
            <CompactView
              appointments={appointments}
              staff={staff}
              services={services}
              currentDate={viewDate}
              filterServiceId={teamFilterServiceId}
              onFilterServiceChange={setTeamFilterServiceId}
              onMoveAppointment={handleTeamMove}
              onClickAppointment={(appt) => openAppointmentDetail(appt)}
              onClickSlot={(member, time) => {
                setAppointmentForm({
                  service_id: services[0]?.service_id || "",
                  staff_id: member.staff_id,
                  client_first_name: "", client_last_name: "", client_email: "", client_phone: "",
                  date: format(viewDate, "yyyy-MM-dd"),
                  time, notes: "", stations_used: 1
                });
                setAppointmentDialogOpen(true);
              }}
              workingHours={{ start: "08:00", end: "20:00" }}
              language={language}
            />
          </div>
        )}
      </div>

      {/* Block Slot Dialog */}
      <Dialog open={blockDialogOpen} onOpenChange={setBlockDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="w-5 h-5" />
              {language === "it" ? "Blocca Slot" : "Block Slot"}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="block text-sm font-medium mb-1.5">{t("staff")} *</label>
              <Select value={blockForm.staff_id} onValueChange={(val) => setBlockForm({ ...blockForm, staff_id: val })}>
                <SelectTrigger data-testid="block-staff-select">
                  <SelectValue placeholder={language === "it" ? "Seleziona operatore" : "Select staff"} />
                </SelectTrigger>
                <SelectContent>
                  {staff.map((s) => (
                    <SelectItem key={s.staff_id} value={s.staff_id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1.5">{language === "it" ? "Data" : "Date"} *</label>
              <Input
                type="date"
                value={blockForm.date}
                onChange={(e) => setBlockForm({ ...blockForm, date: e.target.value })}
                data-testid="block-date-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1.5">{language === "it" ? "Ora Inizio" : "Start Time"} *</label>
                <Input
                  type="time"
                  value={blockForm.start_time}
                  onChange={(e) => setBlockForm({ ...blockForm, start_time: e.target.value })}
                  data-testid="block-start-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5">{language === "it" ? "Ora Fine" : "End Time"} *</label>
                <Input
                  type="time"
                  value={blockForm.end_time}
                  onChange={(e) => setBlockForm({ ...blockForm, end_time: e.target.value })}
                  data-testid="block-end-input"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1.5">{language === "it" ? "Motivo" : "Reason"}</label>
              <Input
                value={blockForm.reason}
                onChange={(e) => setBlockForm({ ...blockForm, reason: e.target.value })}
                placeholder={language === "it" ? "Es: Pausa pranzo, Formazione..." : "E.g.: Lunch break, Training..."}
                data-testid="block-reason-input"
              />
            </div>
            
            {maxStations > 1 && (
              <div>
                <label className="block text-sm font-medium mb-1.5">
                  {language === "it" ? "Postazioni da occupare" : "Stations to block"}
                </label>
                <Select 
                  value={String(blockForm.stations_used)} 
                  onValueChange={(val) => setBlockForm({ ...blockForm, stations_used: parseInt(val) })}
                >
                  <SelectTrigger data-testid="block-stations-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[...Array(maxStations)].map((_, i) => (
                      <SelectItem key={i + 1} value={String(i + 1)}>
                        {i + 1} {language === "it" ? (i === 0 ? "postazione" : "postazioni") : (i === 0 ? "station" : "stations")}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">
                  {language === "it" 
                    ? `La tua attività ha ${maxStations} postazioni disponibili` 
                    : `Your business has ${maxStations} stations available`}
                </p>
              </div>
            )}
            
            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => setBlockDialogOpen(false)}>
                {language === "it" ? "Annulla" : "Cancel"}
              </Button>
              <Button onClick={handleBlockSlot} data-testid="confirm-block-btn">
                <Lock className="w-4 h-4 mr-2" />
                {language === "it" ? "Blocca" : "Block"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* New Appointment Dialog */}
      <Dialog open={appointmentDialogOpen} onOpenChange={setAppointmentDialogOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarIcon className="w-5 h-5" />
              {language === "it" ? "Nuovo Appuntamento" : "New Appointment"}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="block text-sm font-medium mb-1.5">
                {language === "it" ? "Servizi" : "Services"} * 
                <span className="text-xs text-muted-foreground ml-2">
                  ({language === "it" ? "seleziona uno o più" : "select one or more"})
                </span>
              </label>
              
              {/* Selected services badges */}
              {(appointmentForm.service_ids || []).length > 0 && (
                <div className="flex flex-wrap gap-2 mb-2">
                  {(appointmentForm.service_ids || []).map((svcId) => {
                    const svc = services.find(s => s.service_id === svcId);
                    return svc ? (
                      <span key={svcId} className="inline-flex items-center gap-1 px-2 py-1 bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300 rounded-full text-xs">
                        {svc.name} - €{svc.price}
                        <button
                          type="button"
                          onClick={() => setAppointmentForm({
                            ...appointmentForm,
                            service_ids: (appointmentForm.service_ids || []).filter(id => id !== svcId)
                          })}
                          className="ml-1 hover:text-red-500"
                        >
                          ×
                        </button>
                      </span>
                    ) : null;
                  })}
                  <span className="text-xs text-muted-foreground self-center">
                    {language === "it" ? "Totale:" : "Total:"} €{(appointmentForm.service_ids || []).reduce((sum, svcId) => {
                      const svc = services.find(s => s.service_id === svcId);
                      return sum + (svc?.price || 0);
                    }, 0)} | {(appointmentForm.service_ids || []).reduce((sum, svcId) => {
                      const svc = services.find(s => s.service_id === svcId);
                      return sum + (svc?.duration || 0);
                    }, 0)} min
                  </span>
                </div>
              )}
              
              {/* Service selection dropdown */}
              <Select 
                value="" 
                onValueChange={(val) => {
                  if (val && !(appointmentForm.service_ids || []).includes(val)) {
                    setAppointmentForm({ 
                      ...appointmentForm, 
                      service_ids: [...(appointmentForm.service_ids || []), val],
                      service_id: (appointmentForm.service_ids || []).length === 0 ? val : appointmentForm.service_id
                    });
                  }
                }}
              >
                <SelectTrigger data-testid="appt-service-select">
                  <SelectValue placeholder={language === "it" ? "Aggiungi servizio..." : "Add service..."} />
                </SelectTrigger>
                <SelectContent>
                  {services.filter(s => !(appointmentForm.service_ids || []).includes(s.service_id)).map((s) => (
                    <SelectItem key={s.service_id} value={s.service_id}>
                      {s.name} - €{s.price} ({s.duration} min)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1.5">{t("staff")} *</label>
              <Select value={appointmentForm.staff_id} onValueChange={(val) => setAppointmentForm({ ...appointmentForm, staff_id: val })}>
                <SelectTrigger data-testid="appt-staff-select">
                  <SelectValue placeholder={language === "it" ? "Seleziona operatore" : "Select staff"} />
                </SelectTrigger>
                <SelectContent>
                  {staff.map((s) => (
                    <SelectItem key={s.staff_id} value={s.staff_id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1.5">{language === "it" ? "Data" : "Date"} *</label>
                <Input
                  type="date"
                  value={appointmentForm.date}
                  onChange={(e) => setAppointmentForm({ ...appointmentForm, date: e.target.value })}
                  data-testid="appt-date-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5">{language === "it" ? "Ora" : "Time"} *</label>
                <Input
                  type="time"
                  value={appointmentForm.time}
                  onChange={(e) => setAppointmentForm({ ...appointmentForm, time: e.target.value })}
                  data-testid="appt-time-input"
                />
              </div>
            </div>
            
            <div className="border-t pt-4">
              <h4 className="font-medium mb-3">{language === "it" ? "Dati Cliente" : "Client Info"}</h4>
              
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium mb-1.5 flex items-center gap-1">
                      <User className="w-4 h-4" />
                      {language === "it" ? "Nome" : "First Name"} *
                    </label>
                    <Input
                      value={appointmentForm.client_first_name}
                      onChange={(e) => setAppointmentForm({ ...appointmentForm, client_first_name: e.target.value })}
                      placeholder="Mario"
                      data-testid="appt-client-first-name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1.5">
                      {language === "it" ? "Cognome" : "Last Name"} *
                    </label>
                    <Input
                      value={appointmentForm.client_last_name}
                      onChange={(e) => setAppointmentForm({ ...appointmentForm, client_last_name: e.target.value })}
                      placeholder="Rossi"
                      data-testid="appt-client-last-name"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1.5 flex items-center gap-1">
                    <Mail className="w-4 h-4" />
                    Email
                  </label>
                  <Input
                    type="email"
                    value={appointmentForm.client_email}
                    onChange={(e) => setAppointmentForm({ ...appointmentForm, client_email: e.target.value })}
                    placeholder="mario@email.com"
                    data-testid="appt-client-email"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1.5 flex items-center gap-1">
                    <Phone className="w-4 h-4" />
                    {language === "it" ? "Telefono" : "Phone"}
                  </label>
                  <Input
                    value={appointmentForm.client_phone}
                    onChange={(e) => setAppointmentForm({ ...appointmentForm, client_phone: e.target.value })}
                    placeholder="+39 333 1234567"
                    data-testid="appt-client-phone"
                  />
                </div>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1.5">{language === "it" ? "Note" : "Notes"}</label>
              <Input
                value={appointmentForm.notes}
                onChange={(e) => setAppointmentForm({ ...appointmentForm, notes: e.target.value })}
                placeholder={language === "it" ? "Note aggiuntive..." : "Additional notes..."}
                data-testid="appt-notes"
              />
            </div>
            
            {maxStations > 1 && (
              <div>
                <label className="block text-sm font-medium mb-1.5">
                  {language === "it" ? "Postazioni da occupare" : "Stations to use"}
                </label>
                <Select 
                  value={String(appointmentForm.stations_used)} 
                  onValueChange={(val) => setAppointmentForm({ ...appointmentForm, stations_used: parseInt(val) })}
                >
                  <SelectTrigger data-testid="appt-stations-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[...Array(maxStations)].map((_, i) => (
                      <SelectItem key={i + 1} value={String(i + 1)}>
                        {i + 1} {language === "it" ? (i === 0 ? "postazione" : "postazioni") : (i === 0 ? "station" : "stations")}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">
                  {language === "it" 
                    ? `La tua attività ha ${maxStations} postazioni disponibili` 
                    : `Your business has ${maxStations} stations available`}
                </p>
              </div>
            )}
            
            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => setAppointmentDialogOpen(false)}>
                {language === "it" ? "Annulla" : "Cancel"}
              </Button>
              <Button onClick={handleCreateAppointment} data-testid="confirm-appt-btn">
                <Plus className="w-4 h-4 mr-2" />
                {language === "it" ? "Crea Appuntamento" : "Create Appointment"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Appointment/Block Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={(o) => { setDetailDialogOpen(o); if (!o) setDetailEditMode(false); }}>
        <DialogContent className="max-w-md" data-testid="appt-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedAppointment?.is_blocked ? (
                <>
                  <Lock className="w-5 h-5" />
                  {language === "it" ? "Slot Bloccato" : "Blocked Slot"}
                </>
              ) : (
                <>
                  <CalendarIcon className="w-5 h-5" />
                  {detailEditMode
                    ? (language === "it" ? "Modifica appuntamento" : "Edit appointment")
                    : (language === "it" ? "Dettagli Appuntamento" : "Appointment Details")}
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              {selectedAppointment?.client_name || (language === "it" ? "Appuntamento" : "Appointment")}
              {selectedAppointment?.service_name && <span> · {selectedAppointment.service_name}</span>}
            </DialogDescription>
          </DialogHeader>

          {selectedAppointment && (
            <div className="space-y-4 py-2">
              {selectedAppointment.is_blocked ? (
                <>
                  <div className="p-4 rounded-lg bg-muted">
                    <p className="font-medium">{selectedAppointment.client_name}</p>
                    <p className="text-sm text-muted-foreground">
                      {selectedAppointment.date} • {selectedAppointment.time}
                    </p>
                    {selectedAppointment.notes && (
                      <p className="text-sm mt-2">{selectedAppointment.notes}</p>
                    )}
                  </div>

                  <div className="flex justify-end gap-3">
                    <Button variant="outline" onClick={() => setDetailDialogOpen(false)}>
                      {language === "it" ? "Chiudi" : "Close"}
                    </Button>
                    <Button
                      variant="destructive"
                      onClick={() => handleUnblockSlot(selectedAppointment.appointment_id)}
                      data-testid="unblock-btn"
                    >
                      <X className="w-4 h-4 mr-2" />
                      {language === "it" ? "Rimuovi Blocco" : "Remove Block"}
                    </Button>
                  </div>
                </>
              ) : detailEditMode ? (
                // ── EDIT MODE ───────────────────────────────────────
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5 block">
                        {language === "it" ? "Data" : "Date"}
                      </label>
                      <Input type="date" value={detailEditForm.date}
                             onChange={(e) => setDetailEditForm(f => ({ ...f, date: e.target.value }))}
                             data-testid="appt-detail-date" />
                    </div>
                    <div>
                      <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5 block">
                        {language === "it" ? "Ora" : "Time"}
                      </label>
                      <Input type="time" value={detailEditForm.time}
                             onChange={(e) => setDetailEditForm(f => ({ ...f, time: e.target.value }))}
                             data-testid="appt-detail-time" />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5 block">
                      {language === "it" ? "Operatore" : "Staff"}
                    </label>
                    <select value={detailEditForm.staff_id}
                            onChange={(e) => setDetailEditForm(f => ({ ...f, staff_id: e.target.value }))}
                            className="w-full rounded-lg border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900/40 px-3 py-2 text-sm"
                            data-testid="appt-detail-staff">
                      {staff.map(s => (
                        <option key={s.staff_id} value={s.staff_id}>{s.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5 block">
                      {language === "it" ? "Stato" : "Status"}
                    </label>
                    <select value={detailEditForm.status}
                            onChange={(e) => setDetailEditForm(f => ({ ...f, status: e.target.value }))}
                            className="w-full rounded-lg border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900/40 px-3 py-2 text-sm"
                            data-testid="appt-detail-status">
                      <option value="pending">{t("pending")}</option>
                      <option value="awaiting_confirmation">{t("awaiting_confirmation")}</option>
                      <option value="confirmed">{t("confirmed")}</option>
                      <option value="completed">{t("completed")}</option>
                      <option value="cancelled">{t("cancelled")}</option>
                      <option value="no_show">{t("no_show")}</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5 block">
                      {language === "it" ? "Note" : "Notes"}
                    </label>
                    <textarea
                      value={detailEditForm.notes}
                      onChange={(e) => setDetailEditForm(f => ({ ...f, notes: e.target.value }))}
                      rows={3}
                      placeholder={language === "it" ? "Note interne…" : "Internal notes…"}
                      className="w-full rounded-lg border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900/40 px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-200"
                      data-testid="appt-detail-notes"
                    />
                  </div>
                  <div className="flex justify-end gap-2 pt-2">
                    <Button variant="outline" onClick={() => setDetailEditMode(false)}
                            data-testid="appt-detail-cancel-edit">
                      {language === "it" ? "Annulla" : "Cancel"}
                    </Button>
                    <Button
                      onClick={saveDetailEdit}
                      disabled={detailEditSaving || !detailEditForm.date || !detailEditForm.time || !detailEditForm.staff_id}
                      className="bg-gradient-to-r from-blue-500 to-violet-500 text-white"
                      data-testid="appt-detail-save"
                    >
                      {detailEditSaving
                        ? (language === "it" ? "Salvataggio…" : "Saving…")
                        : (language === "it" ? "Salva modifiche" : "Save changes")}
                    </Button>
                  </div>
                </>
              ) : (
                // ── VIEW MODE ───────────────────────────────────────
                <>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center">
                        <User className="w-5 h-5 text-accent" />
                      </div>
                      <div>
                        <p className="font-medium">{selectedAppointment.client_name}</p>
                        <p className="text-sm text-muted-foreground">{selectedAppointment.client_email || selectedAppointment.client_phone || "—"}</p>
                      </div>
                    </div>

                    <div className="p-4 rounded-lg bg-muted space-y-2">
                      <div className="flex items-center gap-2">
                        <Briefcase className="w-4 h-4 text-muted-foreground" />
                        <span>{selectedAppointment.service_name || "Servizio"}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-muted-foreground" />
                        <span>
                          {selectedAppointment.staff_name
                            || staff.find(s => s.staff_id === selectedAppointment.staff_id)?.name
                            || (language === "it" ? "Operatore" : "Staff")}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CalendarIcon className="w-4 h-4 text-muted-foreground" />
                        <span>{selectedAppointment.date}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-muted-foreground" />
                        <span>{selectedAppointment.time} ({selectedAppointment.total_duration || selectedAppointment.duration} min)</span>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm text-muted-foreground">{language === "it" ? "Stato:" : "Status:"}</span>
                      <span className={`px-2 py-0.5 rounded text-xs ${getAppointmentStyle(selectedAppointment)}`}>
                        {selectedAppointment.status}
                      </span>
                      {selectedAppointment.is_manual && (
                        <span className="px-2 py-0.5 rounded text-xs bg-purple-500 text-white">
                          {language === "it" ? "Manuale" : "Manual"}
                        </span>
                      )}
                    </div>

                    {selectedAppointment.notes && (
                      <p className="text-sm text-muted-foreground">
                        <strong>{language === "it" ? "Note:" : "Notes:"}</strong> {selectedAppointment.notes}
                      </p>
                    )}
                  </div>

                  <div className="flex justify-end gap-2 pt-4 flex-wrap">
                    <Button variant="outline" onClick={() => setDetailDialogOpen(false)} data-testid="appt-detail-close">
                      {language === "it" ? "Chiudi" : "Close"}
                    </Button>
                    {selectedAppointment.status !== "cancelled" && selectedAppointment.status !== "completed" && (
                      <Button
                        variant="destructive"
                        onClick={() => handleCancelAppointment(selectedAppointment.appointment_id)}
                        data-testid="cancel-appt-btn"
                      >
                        <X className="w-4 h-4 mr-2" />
                        {language === "it" ? "Cancella" : "Cancel"}
                      </Button>
                    )}
                    <Button
                      onClick={() => setDetailEditMode(true)}
                      className="bg-gradient-to-r from-blue-500 to-violet-500 text-white"
                      data-testid="appt-detail-edit"
                    >
                      {language === "it" ? "Modifica" : "Edit"}
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default CalendarView;
